/**
 * Shows a hint toast pointing users at the command palette (⌘K) when they
 * copy a Modal object ID that the palette can resolve.
 *
 * The hint is shown at most `MAX_SHOWS` times per browser, persisted via
 * localStorage, so once the user has seen it a few times we stop nagging.
 */

import { browser } from "$app/environment";

import { isCommandPaletteAvailable } from "$lib/command-palette/open.svelte";
import { getLocalStore } from "$lib/core/local-storage";
import { getObjectInfo, isResolvableObjectId } from "$lib/core/object-info";
import { hotkeyToString } from "$lib/design-systems/product/foundations/global-hotkey.svelte";
import { makeToast } from "$lib/design-systems/product/primitives/feedback/toast";

const STORAGE_KEY = "modal:cmdk-hint-shown-count";
const MAX_SHOWS = 4;

const shownCount = getLocalStore<number>(STORAGE_KEY, 0);

/**
 * Read the current count straight from `localStorage`. `getLocalStore` caches
 * an in-memory writable per key without subscribing to cross-tab `storage`
 * events, so the cached value can lag behind writes from sibling tabs. Going
 * to `localStorage` here keeps the `MAX_SHOWS` cap accurate when the user has
 * multiple dashboard tabs open at once.
 */
function readShownCount(): number {
  if (!browser) return 0;
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw === null) return 0;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === "number" && Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

/** Reset persisted state — exposed for tests only. */
export function _resetCmdKHintForTesting(): void {
  shownCount.set(0);
}

/**
 * Show the ⌘K hint toast if `text` looks like a resolvable Modal object ID,
 * the command palette is available on the current route, and we haven't
 * already shown the hint `MAX_SHOWS` times. No-op otherwise.
 */
export function maybeShowCmdKHint(text: string | null | undefined): void {
  if (!text) return;
  const trimmed = text.trim();
  if (!isResolvableObjectId(trimmed)) return;
  if (!isCommandPaletteAvailable()) return;

  const current = readShownCount();
  if (current >= MAX_SHOWS) return;
  shownCount.set(current + 1);

  const info = getObjectInfo(trimmed);
  makeToast({
    kind: "info",
    message: `${info.label} ID copied. Press ${hotkeyToString({ ctrlOrCmd: true, hotkey: "k" })} and paste to look it up.`,
  });
}
